import { type Condition, FIELD_DEFINITIONS, type FieldId, type Operator } from "@unbox/api";

export const FIELD_OPTIONS = (Object.keys(FIELD_DEFINITIONS) as FieldId[]).map((id) => ({
  id,
  label: FIELD_DEFINITIONS[id].label,
  supportingText: FIELD_DEFINITIONS[id].tooltip,
}));

export const NUMERIC_OPERATOR_OPTIONS: { id: Operator; label: string }[] = [
  { id: "eq", label: "is" },
  { id: "gt", label: "greater than" },
  { id: "gte", label: "at least" },
  { id: "lt", label: "less than" },
  { id: "lte", label: "at most" },
  { id: "between", label: "between" },
];

export const TAG_OPERATOR_OPTIONS: { id: Operator; label: string }[] = [
  { id: "is", label: "is" },
  { id: "is_not", label: "is not" },
];

// `field` comes from persisted rule JSON, which may still reference a field that has
// since been removed (e.g. the old "tag" condition) — fall back instead of crashing.
export function fieldKind(field: FieldId): "numeric" | "tag" {
  return FIELD_DEFINITIONS[field]?.kind ?? "numeric";
}

export function defaultConditionForField(field: FieldId): Condition {
  // The exact literal shape depends on `field`, which isn't known at this generic
  // call site — each branch below is internally consistent with the discriminated
  // union, so the cast is safe.
  return (
    fieldKind(field) === "tag" ? { field, operator: "is", value: "" } : { field, operator: "gt", value: 0 }
  ) as Condition;
}

export function makeDefaultCondition(): Condition {
  return defaultConditionForField("followerCount");
}

// Tag/Cohort conditions start with an empty value (nothing selected yet), which
// isn't a valid uuid — exclude them from preview/save requests until a value is chosen.
// Also treat a field that no longer exists (e.g. a persisted rule referencing the old
// "tag" condition) as incomplete, so the user must pick a replacement before saving.
export function isConditionComplete(condition: Condition): boolean {
  if (!(condition.field in FIELD_DEFINITIONS)) return false;
  if (fieldKind(condition.field) !== "tag") return true;
  return typeof condition.value === "string" && condition.value.length > 0;
}
